import { Controller } from '@hotwired/stimulus'

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`

const sentence = (items) =>
  items.length > 1 ? `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}` : items.join('')

function compose (facts) {
  const features = []
  if (facts.bedrooms > 0) features.push(plural(facts.bedrooms, 'quarto', 'quartos'))
  if (facts.parking > 0) features.push(plural(facts.parking, 'vaga', 'vagas'))
  if (facts.extra) features.push(facts.extra)

  let title = facts.kind
  if (features.length) title += ` com ${sentence(features)}`
  title += facts.transaction === 'aluguel' ? ' para alugar' : ' à venda'
  if (facts.development) title += ` no ${facts.development}`

  const measures = []
  if (facts.area > 0) measures.push(`${facts.area} m²`)
  if (facts.price > 0) {
    measures.push(`R$ ${facts.price.toLocaleString('pt-BR')}${facts.transaction === 'aluguel' ? '/mês' : ''}`)
  }
  if (measures.length) title += `, ${measures.join(' por ')}`

  const place = [facts.neighborhood, facts.city].filter(Boolean).join(' - ')
  if (place) title += ` - ${place}`

  return title
}

export default class extends Controller {
  static targets = [
    'suggestion', 'transaction', 'kind', 'bedrooms', 'area', 'price', 'priceLabel', 'parking', 'extra',
    'development', 'neighborhood', 'city', 'output', 'length', 'check', 'verdict', 'copied'
  ]

  connect () {
    this.transaction = this.transactionTargets.find((button) => button.getAttribute('aria-pressed') === 'true').dataset.value
    this.refresh()
  }

  pickTransaction ({ currentTarget }) {
    this.transaction = currentTarget.dataset.value
    this.refresh()
  }

  load ({ params: { facts } }) {
    this.transaction = facts.transaction
    this.kindTarget.value = facts.kind
    for (const name of ['bedrooms', 'area', 'price', 'parking', 'development', 'neighborhood', 'city']) {
      this[`${name}Target`].value = facts[name] || ''
    }
    this.extraTarget.value = ''
    this.refresh()
    this.element.querySelector('.title-simulator').scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  refresh () {
    const facts = this.facts
    const title = compose(facts)
    this.outputTarget.textContent = title
    this.lengthTarget.textContent = `${title.length} caracteres`
    this.copiedTarget.textContent = ''

    this.transactionTargets.forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.value === this.transaction))
    })
    this.priceLabelTarget.textContent = this.transaction === 'aluguel' ? 'Aluguel (R$)' : 'Valor de venda (R$)'

    const present = {
      bedrooms: facts.bedrooms > 0, area: facts.area > 0, price: facts.price > 0, neighborhood: !!facts.neighborhood
    }
    this.checkTargets.forEach((chip) => chip.classList.toggle('on', present[chip.dataset.fact]))

    const qualifying = [present.bedrooms, present.area, present.price].filter(Boolean).length
    const detailed = qualifying >= 2 && present.neighborhood
    this.verdictTarget.classList.toggle('ok', detailed)
    this.verdictTarget.textContent =
      detailed
        ? 'Título detalhado: o lead já chega sabendo se o imóvel serve.'
        : qualifying >= 2
          ? 'Quase lá: inclua o bairro.'
          : 'Título simples: inclua pelo menos dois entre quartos, metragem e preço.'
  }

  clear () {
    for (const name of ['bedrooms', 'area', 'price', 'parking', 'extra', 'development', 'neighborhood', 'city']) {
      this[`${name}Target`].value = ''
    }
    this.refresh()
    this.bedroomsTarget.focus()
  }

  copy () {
    this.write(this.outputTarget.textContent, () => { this.copiedTarget.textContent = 'Título copiado' })
  }

  copySuggestion ({ currentTarget }) {
    const suggestion = currentTarget.closest('.lq-row').querySelector('[data-title-simulator-target="suggestion"]')
    this.write(suggestion.textContent, () => {
      const label = currentTarget.textContent
      currentTarget.textContent = 'Copiado ✓'
      setTimeout(() => { currentTarget.textContent = label }, 1500)
    })
  }

  write (text, done) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done)
    } else {
      const area = Object.assign(document.createElement('textarea'), { value: text })
      area.style.position = 'fixed'
      area.style.opacity = '0'
      document.body.appendChild(area)
      area.select()
      document.execCommand('copy')
      area.remove()
      done()
    }
  }

  get facts () {
    const number = (target) => parseInt(target.value, 10) || 0
    return {
      transaction: this.transaction,
      kind: this.kindTarget.value,
      bedrooms: number(this.bedroomsTarget),
      area: number(this.areaTarget),
      price: number(this.priceTarget),
      parking: number(this.parkingTarget),
      extra: this.extraTarget.value.trim(),
      development: this.developmentTarget.value.trim(),
      neighborhood: this.neighborhoodTarget.value.trim(),
      city: this.cityTarget.value.trim()
    }
  }
}
